<script setup lang="ts">
import { liveCapabilities, liveDefinitions, liveMock } from '../data/landing.content'
import { useLandingScroll } from '../composables/useLandingScroll'
import WlIcon from './WlIcon.vue'

/**
 * Seccion "Clases en vivo" (ancla `#clases-en-vivo`).
 *
 * Sigue el mismo esquema que la seccion de cursos: encabezado "que es" a ancho
 * completo, tres definiciones a la izquierda y el mockup de la pantalla de
 * clase en vivo a la derecha.
 *
 * El copy y el mockup se apoyan en el modulo real de clases en vivo
 * (`Core/Features/LiveClasses` y `Core/Features/LiveChat`): la clase tiene
 * titulo, descripcion y una transmision (`StreamUrl`), la publica un
 * instructor, pertenece a un curso y pasa a grabacion cuando termina. El chat
 * pertenece a la clase (`LiveChat.LiveClass`).
 *
 * El mockup es una ilustracion de la interfaz: por eso no publica fechas,
 * horas ni numero de asistentes.
 */
const { registerReveal } = useLandingScroll()
</script>

<template>
  <section class="wl-preview-section" id="clases-en-vivo">
    <div class="container">
      <div class="section-head reveal" :ref="registerReveal">
        <div class="section-tag">Clases en vivo</div>
        <h2 class="section-title">Practica con tu profesor <span class="gradient-text">en vivo</span></h2>
        <p class="section-desc">
          Clases en directo con instructor, chat en tiempo real y grabación para repasar cuando quieras.
        </p>
      </div>

      <!-- Bloque A: que es una clase en vivo. El encabezado va a ancho completo -->
      <div class="wl-liv-block wl-liv-whatis--head">
        <div class="wl-liv-block-head reveal" :ref="registerReveal">
          <span class="section-tag">Qué es</span>
          <h3 class="wl-liv-block-title">
            Una clase en vivo es <span class="gradient-text">el profesor en directo</span>
          </h3>
          <p class="wl-liv-block-desc">
            No es un video más. Es una sesión con hora, con chat y con la
            posibilidad de preguntar en el momento.
          </p>
        </div>
      </div>

      <!-- Showcase: definiciones (vertical) + mockup de la clase en vivo -->
      <div class="wl-liv-showcase reveal" :ref="registerReveal">
        <div class="wl-liv-showcase-side">
          <div class="wl-liv-defs">
            <article
              v-for="(definition, index) in liveDefinitions"
              :key="definition.title"
              class="wl-liv-def reveal"
              :class="`reveal-d${index + 1}`"
              :ref="registerReveal"
            >
              <span class="wl-liv-icon">
                <WlIcon :name="definition.icon" />
              </span>
              <h4>{{ definition.title }}</h4>
              <p>{{ definition.description }}</p>
            </article>
          </div>
        </div>

        <div class="wl-liv-showcase-main">
          <!-- Mockup: como se ve una clase en vivo -->
          <div class="wl-browser-mockup wl-browser-mockup--liv">
            <div class="wl-bm-chrome">
              <div class="wl-bm-dots">
                <span class="wl-bd-r"></span>
                <span class="wl-bd-y"></span>
                <span class="wl-bd-g"></span>
              </div>
              <div class="wl-bm-url">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {{ liveMock.url }}
              </div>
              <div class="wl-bm-ctrl">
                <span></span><span></span><span></span>
              </div>
            </div>

            <div class="wl-liv-body">
              <!-- Reproductor, instructor y clases disponibles -->
              <div class="wl-liv-stage">
                <div class="wl-liv-player">
                  <span class="wl-liv-live-badge">
                    <span class="wl-liv-live-dot"></span>
                    {{ liveMock.playerBadge }}
                  </span>
                  <span class="wl-liv-player-play">
                    <WlIcon name="play" />
                  </span>
                  <span class="wl-liv-player-label">{{ liveMock.playerLabel }}</span>
                </div>

                <div class="wl-liv-instructor">
                  <span class="wl-liv-avatar" aria-hidden="true">{{ liveMock.instructorName.charAt(0) }}</span>
                  <div class="wl-liv-instructor-body">
                    <strong>{{ liveMock.instructorName }}</strong>
                    <span>{{ liveMock.instructorRole }}</span>
                  </div>
                </div>

                <div class="wl-liv-list">
                  <div class="wl-liv-list-head">
                    <span class="wl-liv-list-label">{{ liveMock.listLabel }}</span>
                    <span class="wl-liv-list-note">{{ liveMock.listNote }}</span>
                  </div>

                  <div
                    v-for="item in liveMock.classes"
                    :key="item.title"
                    class="wl-liv-class"
                    :class="`wl-liv-class--${item.state}`"
                  >
                    <div class="wl-liv-class-body">
                      <p class="wl-liv-class-title">{{ item.title }}</p>
                      <p class="wl-liv-class-meta">
                        <span class="wl-liv-class-course">{{ item.course }}</span>
                        <span class="wl-liv-class-dot"></span>
                        <span>{{ item.instructor }}</span>
                      </p>
                    </div>
                    <span class="wl-liv-class-state">
                      <span v-if="item.state === 'live'" class="wl-liv-live-dot"></span>
                      {{ item.stateLabel }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Chat de la clase -->
              <div class="wl-liv-chat">
                <div class="wl-liv-chat-head">
                  <span class="wl-liv-chat-title">
                    <WlIcon name="comment-question" />
                    {{ liveMock.chatTitle }}
                  </span>
                  <span class="wl-liv-chat-count">{{ liveMock.chatCount }}</span>
                </div>

                <div class="wl-liv-chat-body">
                  <p v-for="message in liveMock.messages" :key="message.author + message.text" class="wl-liv-msg">
                    <strong>{{ message.author }}:</strong> <span>{{ message.text }}</span>
                  </p>
                </div>

                <div class="wl-liv-composer">
                  <span class="wl-liv-composer-text">{{ liveMock.composerPlaceholder }}</span>
                  <span class="wl-liv-composer-send">
                    <WlIcon name="arrow-right" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bloque B: que puedes hacer en una clase en vivo -->
      <div class="wl-liv-block wl-liv-block--spaced">
        <div class="wl-liv-block-head reveal" :ref="registerReveal">
          <span class="section-tag">Qué puedes hacer</span>
          <h3 class="wl-liv-block-title">Ocho formas de <span class="gradient-text">participar</span></h3>
          <p class="wl-liv-block-desc">
            La clase en vivo suma a tu curso: no sustituye al temario, lo complementa en directo.
          </p>
        </div>

        <div class="wl-liv-caps reveal" :ref="registerReveal">
          <div v-for="capability in liveCapabilities" :key="capability.title" class="wl-liv-cap">
            <span class="wl-liv-icon wl-liv-icon--sm">
              <WlIcon :name="capability.icon" />
            </span>
            <div class="wl-liv-cap-body">
              <h5>{{ capability.title }}</h5>
              <p>{{ capability.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
